import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
} from "@mui/material";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Label,
} from "recharts";
import {
  Star,
  TrendingUp,
  TrendingDown,
  PieChart as PieChartIconMui,
  BarChart as BarIconMUI,
  WarningAmberOutlined as WarningIcon,
  AccountBalanceWalletOutlined as BalanceIcon,
  AttachMoney,
  Assessment,
  ShowChart as LineStyleIcon,
  GroupWork as GroupIcon,
  ErrorOutline,
  EmojiEvents,
  InfoOutlined as InfoIcon,
  NewspaperOutlined as NewsIcon,
  Insights as InsightsIconMui,
  Category as CategoryIcon,
  SignalCellularAlt as SignalIcon,
  PriceCheck as PriceCheckIcon,
  CardGiftcard as GiftIcon,
  ReportProblemOutlined as ExclamationTriangleIcon,
  Compress as CompressIcon,
  FilterList as FilterListIcon,
  ArrowDownwardSharp as MaxDrawdownIcon,
  NorthEast as GainerIcon,
  SouthWest as LoserIcon,
} from "@mui/icons-material";
import Utils from "../util/Utils";
import { API_KEYS, FMP_API_ENDPOINTS } from "../util/Constants";

const LOCAL_SECTOR_COLORS = [
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#C9CBCF",
  "#77DD77",
  "#B0E0E6",
  "#FFD700",
  "#FF7F50",
  "#DA70D6",
  "#DB7093",
  "#6495ED",
  "#DC143C",
];

const PE_COLORS = ["#2ecc40", "#ffdc00", "#ff851b", "#ff4136", "#800080"];

const getSectorHHI = (data) => {
  if (!data || data.length === 0) return 0;
  const total = data.reduce((sum, s) => {
    const quantity = parseFloat(s.quantity) || 0;
    const currentPrice = parseFloat(s.currentPrice) || 0;
    return sum + quantity * currentPrice;
  }, 0);
  if (total === 0) return 0;
  const sectorShares = {};
  data.forEach((s) => {
    const sector = s.sector || "Unassigned";
    const quantity = parseFloat(s.quantity) || 0;
    const currentPrice = parseFloat(s.currentPrice) || 0;
    const marketValue = quantity * currentPrice;
    sectorShares[sector] = (sectorShares[sector] || 0) + marketValue;
  });
  return Object.values(sectorShares)
    .map((v) => v / total)
    .reduce((sum, share) => sum + share * share, 0);
};

const getTopHoldingsExposure = (data, topN = 5) => {
  if (!data || data.length === 0)
    return { topHoldingsValue: 0, totalPortfolioValue: 0, percentage: 0 };

  // Calculate market value for each stock
  const stocksWithMarketValue = data.map((stock) => {
    const quantity = parseFloat(stock.quantity) || 0;
    const currentPrice = parseFloat(stock.currentPrice) || 0;
    return {
      ...stock,
      marketValue: quantity * currentPrice,
    };
  });

  // Sort by market value and get top N
  const sortedStocks = [...stocksWithMarketValue]
    .filter((stock) => stock.marketValue > 0)
    .sort((a, b) => b.marketValue - a.marketValue)
    .slice(0, topN);

  // Calculate total market value of top N holdings
  const topHoldingsValue = sortedStocks.reduce(
    (sum, stock) => sum + stock.marketValue,
    0
  );

  // Calculate total portfolio value
  const totalPortfolioValue = stocksWithMarketValue.reduce(
    (sum, stock) => sum + stock.marketValue,
    0
  );

  return {
    topHoldingsValue,
    totalPortfolioValue,
    percentage:
      totalPortfolioValue > 0 ? topHoldingsValue / totalPortfolioValue : 0,
  };
};

const getTopContributors = (data, field, topN = 3) => {
  if (!data || data.length === 0) return [];
  const sorted = [...data]
    .filter((s) => typeof s[field] === "number" && !isNaN(s[field]))
    .sort((a, b) => (b[field] || 0) - (a[field] || 0));
  return sorted.slice(0, topN);
};

const getBottomContributors = (data, field, topN = 3) => {
  if (!data || data.length === 0) return [];
  const sorted = [...data]
    .filter((s) => typeof s[field] === "number" && !isNaN(s[field]))
    .sort((a, b) => (a[field] || 0) - (b[field] || 0));
  return sorted.slice(0, topN);
};

const getMaxDrawdown = (data, perfField = "perfYear") => {
  if (!data || data.length === 0) return { value: 0, symbol: null };
  let maxDrawdownValue = 0;
  let stockSymbol = null;
  data.forEach((s) => {
    const perfValue = Number(s[perfField]);
    if (
      s[perfField] !== undefined &&
      !isNaN(perfValue) &&
      perfValue < maxDrawdownValue
    ) {
      maxDrawdownValue = perfValue;
      stockSymbol = s.symbol;
    }
  });
  return { value: maxDrawdownValue, symbol: stockSymbol };
};

const peBucketsData = (portfolioAnalysisData) => {
  const peBuckets = { "<10": 0, "10-20": 0, "20-30": 0, "30-50": 0, "50+": 0 };
  if (!portfolioAnalysisData) return [];
  portfolioAnalysisData.forEach((s) => {
    const pe = Number(s.pe);
    if (isNaN(pe)) return;
    if (pe < 10) peBuckets["<10"]++;
    else if (pe < 20) peBuckets["10-20"]++;
    else if (pe < 30) peBuckets["20-30"]++;
    else if (pe < 50) peBuckets["30-50"]++;
    else if (pe >= 50) peBuckets["50+"]++;
  });
  return Object.entries(peBuckets).map(([name, value], i) => ({
    name,
    value,
    fill: PE_COLORS[i % PE_COLORS.length],
  }));
};

const iconMapMUI = {
  "pie chart": <PieChartIconMui color="primary" />,
  "chart bar outline": <BarIconMUI color="secondary" />,
  "building outline": <GroupIcon color="action" />,
  compress: <CompressIcon color="info" />,
  bullseye: <FilterListIcon color="warning" />,
  "arrow up": <TrendingUp color="success" />,
  "arrow down": <TrendingDown color="error" />,
  "chart line": <LineStyleIcon color="info" />,
  trophy: <EmojiEvents sx={{ color: "goldenrod" }} />,
  "thumbs down outline": <WarningIcon color="error" />,
  "balance scale right": <BalanceIcon color="success" />,
  "balance scale left": <BalanceIcon sx={{ color: "orange" }} />,
  "chart area": <BarIconMUI color="primary" />,
  "dollar sign": <AttachMoney color="success" />,
  "gem outline": <Star sx={{ color: "purple" }} />,
  "angle double down": <MaxDrawdownIcon color="error" />,
  "exclamation triangle": <ExclamationTriangleIcon color="error" />,
  "exclamation circle": <ErrorOutline color="warning" />,
  "info circle": <InfoIcon color="info" />,
  newspaper: <NewsIcon color="action" />,
  cubes: <CategoryIcon color="action" />,
  signal: <SignalIcon color="info" />,
  "search dollar": <PriceCheckIcon color="success" />,
  gift: <GiftIcon color="secondary" />,
  "warning sign": <WarningIcon color="error" />,
};

const renderSectorLabelLocal = (props) => {
  const { cx, cy, midAngle, outerRadius, name, percent, fill } = props;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 28;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const sin = Math.sin(-midAngle * RADIAN);
  const cos = Math.cos(-midAngle * RADIAN);
  const sx = cx + (outerRadius + 8) * cos;
  const sy = cy + (outerRadius + 8) * sin;
  const mx = cx + (outerRadius + 20) * cos;
  const my = cy + (outerRadius + 20) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 15;
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";
  if (percent * 100 < 2) return null;
  return (
    <g>
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2.5} fill={fill} stroke="none" />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 5}
        y={ey}
        textAnchor={textAnchor}
        fill="#444"
        dominantBaseline="central"
        fontSize="11px"
        fontWeight="500"
      >
        {`${name}: ${(percent * 100).toFixed(0)}%`}
      </text>
    </g>
  );
};

const PortfolioInsights = ({
  name,
  portfolioAnalysisData,
  symbolDistributionData,
  portfolioTopGainers,
  portfolioTopLosers,
  BASE_COLORS,
  lightenColor,
  formatDollarValue,
  renderCustomizedLabelWithLines,
  sectorAllocationByMarketValue,
}) => {
  const portfolioName = name;

  const insightsByCategory = useMemo(() => {
    if (!portfolioAnalysisData || portfolioAnalysisData.length === 0)
      return {
        composition: [],
        valuation: [],
        dividends: [],
        shortTermPerformance: [],
        longTermPerformance: [],
      };
    const insights = {
      composition: [],
      valuation: [],
      dividends: [],
      shortTermPerformance: [],
      longTermPerformance: [],
    };
    const totalScreenedStocks = portfolioAnalysisData.length;

    // Sector Analysis
    if (
      sectorAllocationByMarketValue &&
      sectorAllocationByMarketValue.length > 0
    ) {
      const totalMarketValue = portfolioAnalysisData.reduce((sum, s) => {
        const quantity = parseFloat(s.quantity) || 0;
        const currentPrice = parseFloat(s.currentPrice) || 0;
        return sum + quantity * currentPrice;
      }, 0);

      // Top Sector Analysis
      const topSector = sectorAllocationByMarketValue[0];
      const totalSectorValue = sectorAllocationByMarketValue.reduce(
        (sum, sector) => sum + sector.value,
        0
      );
      const topSectorPercentage =
        totalSectorValue > 0 ? (topSector.value / totalSectorValue) * 100 : 0;
      insights.composition.push({
        key: "sector_concentration_top",
        text: `Highest concentration in ${
          topSector.name
        } sector (${topSectorPercentage.toFixed(1)}% - ${formatDollarValue(
          topSector.value
        )}).`,
        iconName: "pie chart",
        colorProp: "primary",
      });

      // Second Largest Sector
      if (sectorAllocationByMarketValue.length > 1) {
        const secondSector = sectorAllocationByMarketValue[1];
        const secondSectorPercentage =
          totalSectorValue > 0
            ? (secondSector.value / totalSectorValue) * 100
            : 0;
        insights.composition.push({
          key: "sector_concentration_second",
          text: `Second largest is ${
            secondSector.name
          } with ${secondSectorPercentage.toFixed(1)}% (${formatDollarValue(
            secondSector.value
          )}).`,
          iconName: "chart bar outline",
          colorProp: "secondary",
        });
      }

      // Sector Diversification Analysis
      const sectorHHI = getSectorHHI(portfolioAnalysisData);
      if (sectorHHI > 0.25) {
        insights.composition.push({
          key: "sector_concentration_risk",
          text: `High sector concentration risk (HHI: ${sectorHHI.toFixed(
            2
          )}). Consider diversifying across more sectors.`,
          iconName: "exclamation triangle",
          colorProp: "error",
        });
      } else if (sectorHHI > 0.15) {
        insights.composition.push({
          key: "sector_concentration_warning",
          text: `Moderate sector concentration (HHI: ${sectorHHI.toFixed(
            2
          )}). Monitor for potential overexposure.`,
          iconName: "warning sign",
          colorProp: "warning",
        });
      }

      // Sector Performance and Valuation Analysis
      const sectorMetrics = {};
      portfolioAnalysisData.forEach((stock) => {
        const sector = stock.sector || "Unassigned";
        if (!sectorMetrics[sector]) {
          sectorMetrics[sector] = {
            count: 0,
            totalPerfYTD: 0,
            totalMarketValue: 0,
            totalPE: 0,
            peCount: 0,
          };
        }
        sectorMetrics[sector].count++;
        sectorMetrics[sector].totalPerfYTD += Number(stock.perfYTD) || 0;
        sectorMetrics[sector].totalMarketValue += Number(stock.marketCap) || 0;
        if (stock.pe && !isNaN(stock.pe)) {
          sectorMetrics[sector].totalPE += Number(stock.pe);
          sectorMetrics[sector].peCount++;
        }
      });

      const sectorAnalysis = Object.entries(sectorMetrics)
        .map(([name, data]) => ({
          name,
          avgPerfYTD: data.count > 0 ? data.totalPerfYTD / data.count : 0,
          marketValue: data.totalMarketValue,
          avgPE: data.peCount > 0 ? data.totalPE / data.peCount : 0,
          count: data.count,
        }))
        .filter((s) => s.count > 0)
        .sort((a, b) => b.marketValue - a.marketValue);

      // Performance Insights
      const performanceBySector = [...sectorAnalysis].sort(
        (a, b) => b.avgPerfYTD - a.avgPerfYTD
      );

      // Valuation Insights
      const valuationBySector = sectorAnalysis.filter((s) => s.avgPE > 0);
      if (valuationBySector.length > 0) {
        const highestValuedSector = valuationBySector[0];
        insights.valuation.push({
          key: "sector_valuation",
          text: `Highest valued sector: ${
            highestValuedSector.name
          } with average P/E of ${highestValuedSector.avgPE.toFixed(1)}.`,
          iconName: "chart area",
          colorProp: "primary",
        });
      }
    }

    // Calculate portfolio values using symbolDistributionData (user's portfolio)
    const totalPortfolioValue =
      symbolDistributionData?.reduce((sum, stock) => sum + stock.value, 0) || 0;
    let top5Percentage = 0;

    if (symbolDistributionData && symbolDistributionData.length > 0) {
      const top5Holdings = [...symbolDistributionData]
        .sort((a, b) => b.value - a.value)
        .slice(0, 5);

      const top5Value = top5Holdings.reduce(
        (sum, stock) => sum + stock.value,
        0
      );
      top5Percentage =
        totalPortfolioValue > 0 ? (top5Value / totalPortfolioValue) * 100 : 0;

      insights.composition.push({
        key: "top5_exposure",
        text: `Top 5 holdings (${formatDollarValue(
          top5Value
        )}) represent ${top5Percentage.toFixed(
          1
        )}% of total portfolio value (${formatDollarValue(
          totalPortfolioValue
        )}). ${
          top5Percentage > 50
            ? "High concentration risk - consider diversifying."
            : top5Percentage > 30
            ? "Moderate concentration - monitor for overexposure."
            : "Well-diversified across holdings."
        }`,
        iconName: "bullseye",
        colorProp:
          top5Percentage > 50
            ? "error"
            : top5Percentage > 30
            ? "warning"
            : "success",
      });
    }

    if (top5Percentage > 50) {
      insights.composition.push({
        key: "top5_concentration_warning",
        text: "High concentration in top 5 holdings by portfolio value. Review for overexposure.",
        iconName: "exclamation circle",
        colorProp: "warning",
      });
    }

    const undervaluedStocks = portfolioAnalysisData.filter(
      (s) =>
        s.discIntrinsic > 0 &&
        s.reasonablyPriced &&
        s.reasonablyPriced.toLowerCase().includes("undervalued")
    );
    if (undervaluedStocks.length > 0) {
      insights.valuation.push({
        key: "undervalued",
        text: `Potentially undervalued stocks: ${undervaluedStocks
          .map((s) => s.symbol)
          .join(", ")}.`,
        iconName: "balance scale right",
        colorProp: "success",
      });
    }

    const overvaluedStocks = portfolioAnalysisData.filter(
      (s) =>
        s.discIntrinsic < 0 &&
        s.reasonablyPriced &&
        s.reasonablyPriced.toLowerCase().includes("overvalued")
    );
    if (overvaluedStocks.length > 0) {
      insights.valuation.push({
        key: "overvalued",
        text: `Potentially overvalued stocks: ${overvaluedStocks
          .map((s) => s.symbol)
          .join(", ")}.`,
        iconName: "balance scale left",
        colorProp: "error",
      });
    }

    insights.valuation.push({
      key: "pe_distribution_summary",
      text: `P/E ratio distribution: ${peBucketsData(portfolioAnalysisData)
        .filter((d) => d.value > 0)
        .map((d) => `${d.name} (${d.value})`)
        .join(", ")}.`,
      iconName: "chart area",
      colorProp: "primary",
    });

    const dividendPayers = portfolioAnalysisData.filter(
      (s) => (Number(s.dividendYield) || 0) > 0
    );
    if (dividendPayers.length > 0) {
      const avgDividendYield =
        dividendPayers.reduce(
          (sum, s) => sum + (Number(s.dividendYield) || 0),
          0
        ) / dividendPayers.length;
      insights.dividends.push({
        key: "dividend_payers",
        text: `Dividend-paying stocks (${dividendPayers
          .map((s) => s.symbol)
          .join(", ")}). Average yield: ${Utils.format_ratio(
          avgDividendYield
        )}.`,
        iconName: "dollar sign",
        colorProp: "success",
      });
      const topDivYielders = getTopContributors(
        dividendPayers,
        "dividendYield",
        3
      );
      const totalPortfolioDividendYieldFromPayers = dividendPayers.reduce(
        (sum, s) => sum + (s.dividendYield || 0),
        0
      );
      if (
        topDivYielders.length > 0 &&
        totalPortfolioDividendYieldFromPayers > 0
      ) {
        const top3YieldSum = topDivYielders.reduce(
          (sum, s) => sum + (s.dividendYield || 0),
          0
        );
        const top3Contribution =
          (top3YieldSum / totalPortfolioDividendYieldFromPayers) * 100;
        insights.dividends.push({
          key: "dividend_concentration",
          text: `Top dividend payers (${topDivYielders
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.dividendYield)})`)
            .join(", ")}) contribute ${top3Contribution.toFixed(
            1
          )}% of the total yield from dividend-paying stocks in this selection.`,
          iconName: "gem outline",
          colorProp: "warning",
        });
      }
    } else {
      insights.dividends.push({
        key: "no_dividend_payers",
        text: "No stocks in this selection offer a dividend yield.",
        iconName: "info circle",
        colorProp: "info",
      });
    }

    // Add Short Term Performance Insights
    if (portfolioAnalysisData && portfolioAnalysisData.length > 0) {
      // POSITIVE INSIGHTS SECTION
      // Overall Positive Trend Analysis
      const stocksWithPositiveTrend = portfolioAnalysisData.filter(
        (s) =>
          (s.perfWeek > 0 && s.perfMonth > 0) ||
          (s.perfMonth > 0 && s.perfYTD > 0)
      ).length;

      if (stocksWithPositiveTrend > 0) {
        insights.shortTermPerformance.push({
          key: "positive_trend",
          text: `${stocksWithPositiveTrend} stocks showing consistent positive momentum across multiple timeframes.`,
          iconName: "signal",
          colorProp: "success",
        });
      }

      // Weekly Top Performers
      const weeklyPerformers = getTopContributors(
        portfolioAnalysisData,
        "perfWeek",
        3
      );
      if (weeklyPerformers.length > 0) {
        insights.shortTermPerformance.push({
          key: "weekly_top_performers",
          text: `Top weekly performers: ${weeklyPerformers
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfWeek)})`)
            .join(", ")}.`,
          iconName: "arrow up",
          colorProp: "success",
        });
      }

      // Monthly Top Performers
      const monthlyPerformers = getTopContributors(
        portfolioAnalysisData,
        "perfMonth",
        3
      );
      if (monthlyPerformers.length > 0) {
        insights.shortTermPerformance.push({
          key: "monthly_top_performers",
          text: `Top monthly performers: ${monthlyPerformers
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfMonth)})`)
            .join(", ")}.`,
          iconName: "chart line",
          colorProp: "primary",
        });
      }

      // YTD Top Performers
      const ytdPerformers = getTopContributors(
        portfolioAnalysisData,
        "perfYTD",
        3
      );
      if (ytdPerformers.length > 0) {
        insights.shortTermPerformance.push({
          key: "ytd_top_performers",
          text: `Top YTD performers: ${ytdPerformers
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfYTD)})`)
            .join(", ")}.`,
          iconName: "trophy",
          colorProp: "warning",
        });
      }

      // Add divider between positive and negative insights
      if (insights.shortTermPerformance.length > 0) {
        insights.shortTermPerformance.push({
          key: "divider",
          text: "Areas of Concern",
          colorProp: "error",
          isDivider: true,
        });
      }

      // WARNINGS SECTION
      // Weekly Warnings
      const weeklyLosers = getBottomContributors(
        portfolioAnalysisData,
        "perfWeek",
        3
      );
      const significantWeeklyLosses = weeklyLosers.filter(
        (s) => s.perfWeek < -0.05
      );
      if (significantWeeklyLosses.length > 0) {
        insights.shortTermPerformance.push({
          key: "weekly_significant_losses",
          text: `Warning: ${
            significantWeeklyLosses.length
          } stocks down more than 5% this week: ${significantWeeklyLosses
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfWeek)})`)
            .join(", ")}.`,
          iconName: "warning sign",
          colorProp: "error",
        });
      }

      // Monthly Warnings
      const monthlyLosers = getBottomContributors(
        portfolioAnalysisData,
        "perfMonth",
        3
      );
      const significantMonthlyLosses = monthlyLosers.filter(
        (s) => s.perfMonth < -0.1
      );
      if (significantMonthlyLosses.length > 0) {
        insights.shortTermPerformance.push({
          key: "monthly_significant_losses",
          text: `Warning: ${
            significantMonthlyLosses.length
          } stocks down more than 10% this month: ${significantMonthlyLosses
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfMonth)})`)
            .join(", ")}.`,
          iconName: "warning sign",
          colorProp: "error",
        });
      }

      // YTD Warnings
      const ytdLosers = getBottomContributors(
        portfolioAnalysisData,
        "perfYTD",
        3
      );
      const significantYTDLosses = ytdLosers.filter((s) => s.perfYTD < -0.15);
      if (significantYTDLosses.length > 0) {
        insights.shortTermPerformance.push({
          key: "ytd_significant_losses",
          text: `Warning: ${
            significantYTDLosses.length
          } stocks down more than 15% YTD: ${significantYTDLosses
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfYTD)})`)
            .join(", ")}.`,
          iconName: "warning sign",
          colorProp: "error",
        });
      }

      // Negative Trends Warning
      const stocksWithNegativeTrend = portfolioAnalysisData.filter(
        (s) =>
          (s.perfWeek < 0 && s.perfMonth < 0) ||
          (s.perfMonth < 0 && s.perfYTD < 0)
      );
      if (stocksWithNegativeTrend.length > 0) {
        insights.shortTermPerformance.push({
          key: "negative_trend",
          text: `Stocks showing consistent negative momentum: ${stocksWithNegativeTrend
            .map((s) => s.symbol)
            .join(", ")}.`,
          iconName: "exclamation triangle",
          colorProp: "error",
        });
      }

      // High Volatility Warning
      const volatileStocks = portfolioAnalysisData.filter(
        (s) => Math.abs(s.perfWeek) > 0.1 && Math.abs(s.perfMonth) > 0.15
      );
      if (volatileStocks.length > 0) {
        insights.shortTermPerformance.push({
          key: "high_volatility",
          text: `High volatility stocks: ${volatileStocks
            .map((s) => s.symbol)
            .join(", ")}.`,
          iconName: "exclamation circle",
          colorProp: "warning",
        });
      }
    }

    // Add Long Term Performance Insights
    if (portfolioAnalysisData && portfolioAnalysisData.length > 0) {
      // POSITIVE INSIGHTS SECTION
      // Overall Long-term Positive Trend Analysis
      const stocksWithLongTermPositiveTrend = portfolioAnalysisData.filter(
        (s) =>
          (s.perfYear > 0 && s.perf5Year > 0) ||
          (s.perf5Year > 0 && s.perf10Year > 0)
      );

      if (stocksWithLongTermPositiveTrend.length > 0) {
        insights.longTermPerformance.push({
          key: "long_term_positive_trend",
          text: `Stocks showing consistent positive momentum over multiple years: ${stocksWithLongTermPositiveTrend
            .map((s) => s.symbol)
            .join(", ")}.`,
          iconName: "signal",
          colorProp: "success",
        });
      }

      // 1-Year Top Performers
      const yearPerformers = getTopContributors(
        portfolioAnalysisData,
        "perfYear",
        3
      );
      if (yearPerformers.length > 0) {
        insights.longTermPerformance.push({
          key: "year_top_performers",
          text: `Top 1-year performers: ${yearPerformers
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfYear)})`)
            .join(", ")}.`,
          iconName: "chart line",
          colorProp: "primary",
        });
      }

      // 5Y (annualized) top performers
      const fiveYearPerformers = getTopContributors(
        portfolioAnalysisData,
        "perf5Year",
        3
      );
      if (fiveYearPerformers.length > 0) {
        insights.longTermPerformance.push({
          key: "five_year_top_performers",
          text: `Top 5Y (annualized) performers: ${fiveYearPerformers
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perf5Year)})`)
            .join(", ")}.`,
          iconName: "trophy",
          colorProp: "warning",
        });
      }

      // 10Y (annualized) top performers
      const tenYearPerformers = getTopContributors(
        portfolioAnalysisData,
        "perf10Year",
        3
      );
      if (tenYearPerformers.length > 0) {
        insights.longTermPerformance.push({
          key: "ten_year_top_performers",
          text: `Top 10Y (annualized) performers: ${tenYearPerformers
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perf10Year)})`)
            .join(", ")}.`,
          iconName: "trophy",
          colorProp: "warning",
        });
      }

      // Add divider between positive and negative insights
      if (insights.longTermPerformance.length > 0) {
        insights.longTermPerformance.push({
          key: "divider",
          text: "Areas of Concern",
          colorProp: "error",
          isDivider: true,
        });
      }

      // WARNINGS SECTION
      // 1-Year Warnings
      const yearLosers = getBottomContributors(
        portfolioAnalysisData,
        "perfYear",
        3
      );
      const significantYearLosses = yearLosers.filter((s) => s.perfYear < -0.2);
      if (significantYearLosses.length > 0) {
        insights.longTermPerformance.push({
          key: "year_significant_losses",
          text: `Warning: ${
            significantYearLosses.length
          } stocks down more than 20% over the past year: ${significantYearLosses
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfYear)})`)
            .join(", ")}.`,
          iconName: "warning sign",
          colorProp: "error",
        });
      }

      // 5Y (annualized) warnings
      const fiveYearLosers = getBottomContributors(
        portfolioAnalysisData,
        "perf5Year",
        3
      );
      const significantFiveYearLosses = fiveYearLosers.filter(
        (s) => s.perf5Year < -0.3
      );
      if (significantFiveYearLosses.length > 0) {
        insights.longTermPerformance.push({
          key: "five_year_significant_losses",
          text: `Warning: ${
            significantFiveYearLosses.length
          } stocks with 5Y annualized return below -30%: ${significantFiveYearLosses
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perf5Year)})`)
            .join(", ")}.`,
          iconName: "warning sign",
          colorProp: "error",
        });
      }

      // 10Y (annualized) warnings
      const tenYearLosers = getBottomContributors(
        portfolioAnalysisData,
        "perf10Year",
        3
      );
      const significantTenYearLosses = tenYearLosers.filter(
        (s) => s.perf10Year < -0.4
      );
      if (significantTenYearLosses.length > 0) {
        insights.longTermPerformance.push({
          key: "ten_year_significant_losses",
          text: `Warning: ${
            significantTenYearLosses.length
          } stocks with 10Y annualized return below -40%: ${significantTenYearLosses
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perf10Year)})`)
            .join(", ")}.`,
          iconName: "warning sign",
          colorProp: "error",
        });
      }

      // Long-term Negative Trends Warning
      const stocksWithLongTermNegativeTrend = portfolioAnalysisData.filter(
        (s) =>
          (s.perfYear < 0 && s.perf5Year < 0) ||
          (s.perf5Year < 0 && s.perf10Year < 0)
      );
      if (stocksWithLongTermNegativeTrend.length > 0) {
        insights.longTermPerformance.push({
          key: "long_term_negative_trend",
          text: `Stocks with long-term negative momentum: ${stocksWithLongTermNegativeTrend
            .map((s) => s.symbol)
            .join(", ")}.`,
          iconName: "exclamation triangle",
          colorProp: "error",
        });
      }

      // Long-term Volatility Warning
      const longTermVolatileStocks = portfolioAnalysisData.filter(
        (s) => Math.abs(s.perfYear) > 0.3 && Math.abs(s.perf5Year) > 0.5
      );
      if (longTermVolatileStocks.length > 0) {
        insights.longTermPerformance.push({
          key: "long_term_volatility",
          text: `Stocks with high long-term volatility: ${longTermVolatileStocks
            .map((s) => s.symbol)
            .join(", ")}.`,
          iconName: "exclamation circle",
          colorProp: "warning",
        });
      }
    }

    // Update Valuation & Financial Health insights to remove redundancies
    if (portfolioAnalysisData && portfolioAnalysisData.length > 0) {
      // Market Cap Distribution Analysis
      const marketCapDistribution = portfolioAnalysisData.reduce(
        (acc, stock) => {
          const size = stock.companySize || "N/A";
          acc[size] = (acc[size] || 0) + 1;
          return acc;
        },
        {}
      );

      const dominantSize = Object.entries(marketCapDistribution).sort(
        ([, a], [, b]) => b - a
      )[0]?.[0];

      if (dominantSize) {
        insights.valuation.push({
          key: "market_cap_distribution",
          text: `Portfolio is primarily composed of ${dominantSize} companies, indicating ${
            dominantSize === "Large" ? "lower" : "higher"
          } risk/reward potential.`,
          iconName: "building outline",
          colorProp: dominantSize === "Large" ? "success" : "warning",
        });
      }

      // Valuation Analysis
      const avgPE =
        portfolioAnalysisData.reduce(
          (sum, stock) => sum + (Number(stock.pe) || 0),
          0
        ) /
        portfolioAnalysisData.filter((stock) => stock.pe && !isNaN(stock.pe))
          .length;

      if (!isNaN(avgPE)) {
        insights.valuation.push({
          key: "avg_pe",
          text: `Average P/E ratio of ${avgPE.toFixed(1)}x, suggesting ${
            avgPE < 15
              ? "potentially undervalued"
              : avgPE < 25
              ? "fairly valued"
              : "potentially overvalued"
          } portfolio.`,
          iconName: "chart area",
          colorProp: avgPE < 15 ? "success" : avgPE < 25 ? "info" : "warning",
        });
      }

      // Add divider between positive and negative insights
      if (insights.valuation.length > 0) {
        insights.valuation.push({
          key: "divider",
          text: "Areas of Concern",
          colorProp: "error",
          isDivider: true,
        });
      }

      // Risk Analysis - Keep only extreme valuation warnings
      const extremeValuationStocks = portfolioAnalysisData.filter(
        (stock) => stock.pe >= 100
      );
      if (extremeValuationStocks.length > 0) {
        insights.valuation.push({
          key: "extreme_valuation_warning",
          text: `Stocks with extreme P/E ratios (>100x): ${extremeValuationStocks
            .map((s) => s.symbol)
            .join(", ")}.`,
          iconName: "exclamation triangle",
          colorProp: "error",
        });
      }
    }

    return insights;
  }, [portfolioAnalysisData, sectorAllocationByMarketValue]);

  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState(null);

  const fetchNews = async (symbol) => {
    try {
      const response = await fetch(
        `${FMP_API_ENDPOINTS.STOCK_NEWS()}?${
          API_KEYS.FMP_API_KEY
        }&symbols=${symbol}&limit=2`
      );
      if (!response.ok) throw new Error(`Failed to fetch news for ${symbol}`);
      const data = await response.json();
      const sortedNews = data.sort(
        (a, b) => new Date(b.publishedDate) - new Date(a.publishedDate)
      );
      setNews(sortedNews.slice(0, 5));
    } catch (error) {
      console.error(`Error fetching news for ${symbol}:`, error);
      setNewsError("Failed to load some news articles.");
    } finally {
      setNewsLoading(false);
    }
  };

  useEffect(() => {
    const SIGNIFICANT_MOVE = 0.03;
    const currentData = portfolioAnalysisData || [];
    const sortedByMarketCap = [...currentData]
      .filter((s) => s.marketCap > 0)
      .sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0));
    const topHoldings = sortedByMarketCap.slice(0, 5);
    const movedSymbols = topHoldings
      .filter(
        (s) =>
          typeof s.perfWeek === "number" &&
          Math.abs(s.perfWeek) >= SIGNIFICANT_MOVE
      )
      .map((s) => s.symbol);
    if (movedSymbols.length > 0) {
      setNewsLoading(true);
      setNewsError(null);
      setNews([]);
      Promise.all(movedSymbols.map((symbol) => fetchNews(symbol)))
        .then(() => {
          // No need to handle the result of fetchNews
        })
        .catch((err) => {
          console.error("Overall news fetching error:", err);
          setNewsError("Failed to load some news articles.");
        });
    } else {
      setNews([]);
    }
  }, [portfolioAnalysisData]);

  const noDataForUserPortfolioCharts =
    (!symbolDistributionData || symbolDistributionData.length === 0) &&
    (!portfolioTopGainers || portfolioTopGainers.length === 0) &&
    (!portfolioTopLosers || portfolioTopLosers.length === 0);

  const noDataForScreenerCharts =
    !portfolioAnalysisData || portfolioAnalysisData.length === 0;

  if (noDataForUserPortfolioCharts && noDataForScreenerCharts) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: "680px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: 3, textAlign: "center" }}>
          <InsightsIconMui
            sx={{ fontSize: 60, color: "action.disabled", mb: 2 }}
          />
          <Typography variant="h5" gutterBottom>
            No Data for Analysis
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Portfolio data is not available to generate insights or charts. Add
            stocks to your portfolio or ensure screener data is loaded.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const insightCategoriesConfig = [
    { title: "Portfolio Composition", key: "composition", icon: <GroupIcon /> },
    {
      title: "Valuation & Financial Health",
      key: "valuation",
      icon: <PriceCheckIcon />,
    },
    { title: "Dividend Focus", key: "dividends", icon: <AttachMoney /> },
    {
      title: "Short Term Performance",
      key: "shortTermPerformance",
      icon: <TrendingUp />,
    },
    {
      title: "Long Term Performance",
      key: "longTermPerformance",
      icon: <LineStyleIcon />,
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 1, md: 2 },
        minHeight: { xs: "auto", md: "680px" },
        backgroundColor: "#f9fafb",
        mb: { xs: "80px", md: 0 },
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        align="center"
        sx={{
          color: "primary.dark",
          fontWeight: 600,
          mb: 2,
          mt: 0.5,
          fontSize: { xs: "1.5rem", md: "1.75rem" },
          letterSpacing: "-0.02em",
        }}
      >
        <InsightsIconMui
          sx={{
            verticalAlign: "middle",
            mr: 1,
            fontSize: "1.75rem",
            color: "primary.main",
          }}
        />
        {portfolioName} Analysis & Insights
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {symbolDistributionData && symbolDistributionData.length > 0 && (
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: "100%",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                },
              }}
            >
              <Typography
                variant="h6"
                component="h2"
                gutterBottom
                align="center"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  color: "primary.dark",
                  mb: 1,
                }}
              >
                <PieChartIconMui sx={{ mr: 1, color: "primary.main" }} /> Symbol
                Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPieChart
                  margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                >
                  <defs>
                    {symbolDistributionData.map((entry, index) => (
                      <radialGradient
                        id={`user-portfolio-grad-sym-${index}`}
                        key={`user-portfolio-grad-sym-${index}`}
                        cx="50%"
                        cy="50%"
                        r="50%"
                        fx="25%"
                        fy="25%"
                      >
                        <stop
                          offset="0%"
                          stopColor={lightenColor(
                            BASE_COLORS[index % BASE_COLORS.length],
                            20
                          )}
                        />
                        <stop
                          offset="100%"
                          stopColor={BASE_COLORS[index % BASE_COLORS.length]}
                        />
                      </radialGradient>
                    ))}
                  </defs>
                  <Pie
                    data={symbolDistributionData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabelWithLines}
                    outerRadius={80}
                    innerRadius={40}
                    dataKey="value"
                    nameKey="name"
                    paddingAngle={1}
                    stroke="#fff"
                    strokeWidth={1}
                  >
                    {symbolDistributionData.map((entry, index) => (
                      <Cell
                        key={`user-portfolio-c-sym-${index}`}
                        fill={`url(#user-portfolio-grad-sym-${index})`}
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(value, name) => [
                      `${formatDollarValue(value)} (${name})`,
                      "Market Value",
                    ]}
                  />
                  <Legend
                    iconType="circle"
                    wrapperStyle={{
                      fontSize: "10px",
                      paddingTop: "15px",
                      lineHeight: "1.2",
                    }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}

        {sectorAllocationByMarketValue &&
          sectorAllocationByMarketValue.length > 0 && (
            <Grid
              item
              xs={12}
              md={
                symbolDistributionData && symbolDistributionData.length > 0
                  ? 4
                  : 6
              }
            >
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  height: "100%",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  align="center"
                  sx={{
                    color: "primary.dark",
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  <PieChartIconMui sx={{ verticalAlign: "middle", mr: 1 }} />
                  Sector Allocation (Market Value)
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <RechartsPieChart
                    margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                  >
                    <defs>
                      {sectorAllocationByMarketValue.map((entry, index) => (
                        <radialGradient
                          id={`gradient-sector-analysis-${index}`}
                          key={`gradient-sector-analysis-${index}`}
                          cx="50%"
                          cy="50%"
                          r="50%"
                          fx="30%"
                          fy="30%"
                        >
                          <stop
                            offset="0%"
                            stopColor={lightenColor(
                              LOCAL_SECTOR_COLORS[
                                index % LOCAL_SECTOR_COLORS.length
                              ],
                              25
                            )}
                          />
                          <stop
                            offset="100%"
                            stopColor={
                              LOCAL_SECTOR_COLORS[
                                index % LOCAL_SECTOR_COLORS.length
                              ]
                            }
                          />
                        </radialGradient>
                      ))}
                    </defs>
                    <Pie
                      data={sectorAllocationByMarketValue}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabelWithLines}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {sectorAllocationByMarketValue.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={`url(#gradient-sector-analysis-${index})`}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value) => formatDollarValue(value)}
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "8px",
                      }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

        {peBucketsData(portfolioAnalysisData).some((b) => b.value > 0) && (
          <Grid
            item
            xs={12}
            md={
              symbolDistributionData && symbolDistributionData.length > 0
                ? 4
                : sectorAllocationByMarketValue &&
                  sectorAllocationByMarketValue.length > 0
                ? 6
                : 12
            }
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                height: "100%",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                },
              }}
            >
              <Typography
                variant="h6"
                component="h2"
                gutterBottom
                align="center"
                sx={{
                  color: "secondary.dark",
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                <GroupIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                Distribution by Company Size
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={[
                    {
                      name: "Mega Cap",
                      value: portfolioAnalysisData.filter(
                        (s) => s.companySize === "Mega"
                      ).length,
                      fill: "#0074D9",
                    },
                    {
                      name: "Large Cap",
                      value: portfolioAnalysisData.filter(
                        (s) => s.companySize === "Large"
                      ).length,
                      fill: "#2ecc40",
                    },
                    {
                      name: "Mid Cap",
                      value: portfolioAnalysisData.filter(
                        (s) => s.companySize === "Mid"
                      ).length,
                      fill: "#ffdc00",
                    },
                    {
                      name: "Small Cap",
                      value: portfolioAnalysisData.filter(
                        (s) => s.companySize === "Small"
                      ).length,
                      fill: "#ff851b",
                    },
                    {
                      name: "Micro Cap",
                      value: portfolioAnalysisData.filter(
                        (s) => s.companySize === "Micro"
                      ).length,
                      fill: "#ff4136",
                    },
                  ]}
                  margin={{ top: 15, right: 10, left: -15, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" fontSize="0.75rem" />
                  <YAxis allowDecimals={false} fontSize="0.75rem" />
                  <RechartsTooltip
                    formatter={(value, name) => [`${value} stocks`, name]}
                    contentStyle={{
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      border: "none",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {[
                      { name: "Mega Cap", fill: "#0074D9" },
                      { name: "Large Cap", fill: "#2ecc40" },
                      { name: "Mid Cap", fill: "#ffdc00" },
                      { name: "Small Cap", fill: "#ff851b" },
                      { name: "Micro Cap", fill: "#ff4136" },
                    ].map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                    <Label
                      position="top"
                      content={({ value }) => (
                        <text
                          x={0}
                          y={-10}
                          fill="#666"
                          textAnchor="middle"
                          fontSize={12}
                        >
                          {value}
                        </text>
                      )}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Box sx={{ mb: 2 }}>
        {(portfolioTopGainers?.length > 0 ||
          portfolioTopLosers?.length > 0) && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Stack spacing={2.5}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      mb={2}
                      sx={{
                        pb: 1.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <GainerIcon
                        sx={{ color: "success.main", fontSize: "1.8rem" }}
                      />
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{
                          fontWeight: 600,
                          color: "primary.dark",
                        }}
                      >
                        Top Gainers
                      </Typography>
                    </Stack>
                    <List dense disablePadding>
                      {portfolioTopGainers?.map((stock) => (
                        <ListItem
                          key={`gain-${stock.symbol}`}
                          sx={{
                            py: 0.5,
                            px: 1.5,
                            borderRadius: 1,
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                            "&:not(:last-child)": {
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: "success.main",
                                  }}
                                >
                                  {stock.symbol}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "success.main",
                                    fontWeight: 500,
                                  }}
                                >
                                  {Utils.format_ratio(stock.percentageGainLoss)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>

                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      mb={2}
                      sx={{
                        pb: 1.5,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <LoserIcon
                        sx={{ color: "error.main", fontSize: "1.8rem" }}
                      />
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{
                          fontWeight: 600,
                          color: "primary.dark",
                        }}
                      >
                        Top Losers
                      </Typography>
                    </Stack>
                    <List dense disablePadding>
                      {portfolioTopLosers?.map((stock) => (
                        <ListItem
                          key={`loss-${stock.symbol}`}
                          sx={{
                            py: 0.5,
                            px: 1.5,
                            borderRadius: 1,
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                            "&:not(:last-child)": {
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: "error.main",
                                  }}
                                >
                                  {stock.symbol}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "error.main",
                                    fontWeight: 500,
                                  }}
                                >
                                  {Utils.format_ratio(stock.percentageGainLoss)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            {insightCategoriesConfig
              .filter(
                (category) =>
                  category.key === "shortTermPerformance" ||
                  category.key === "longTermPerformance"
              )
              .map((category) => {
                const categoryInsights = insightsByCategory[category.key];
                if (!categoryInsights || categoryInsights.length === 0)
                  return null;
                return (
                  <Grid item xs={12} sm={6} md={4} key={category.key}>
                    <Card
                      elevation={0}
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 2 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          mb={1.5}
                          sx={{
                            pb: 1,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          {React.cloneElement(category.icon, {
                            color: "primary",
                            sx: { fontSize: "1.8rem" },
                          })}
                          <Typography
                            variant="h6"
                            component="div"
                            sx={{
                              fontWeight: 600,
                              color: "primary.dark",
                            }}
                          >
                            {category.title}
                          </Typography>
                        </Stack>
                        <List dense disablePadding>
                          {categoryInsights.map((insight) => (
                            <ListItem
                              key={insight.key}
                              sx={{
                                py: insight.isDivider ? 0.75 : 0.5,
                                px: 1.5,
                                borderRadius: 1,
                                backgroundColor: insight.isDivider
                                  ? "rgba(255, 235, 235, 0.9)"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: insight.isDivider
                                    ? "rgba(255, 235, 235, 0.9)"
                                    : "action.hover",
                                },
                                "&:not(:last-child)": {
                                  borderBottom: "1px solid",
                                  borderColor: "divider",
                                },
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 38,
                                  mt: 0.3,
                                  color: `${
                                    insight.colorProp || "action"
                                  }.main`,
                                }}
                              >
                                {React.cloneElement(
                                  iconMapMUI[insight.iconName] || <InfoIcon />,
                                  { fontSize: "small" }
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primaryTypographyProps={{
                                  variant: "body2",
                                  lineHeight: 1.2,
                                  fontSize: "0.95rem",
                                  color: insight.isDivider
                                    ? "error.main"
                                    : "text.secondary",
                                  fontWeight: insight.isDivider ? 600 : 400,
                                  sx: insight.isDivider
                                    ? {
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                      }
                                    : {},
                                }}
                                primary={insight.text}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>
        )}
      </Box>

      <Grid container spacing={2}>
        {insightCategoriesConfig
          .filter(
            (category) =>
              category.key === "valuation" ||
              category.key === "composition" ||
              category.key === "dividends" ||
              category.key === "financialHealth"
          )
          .map((category) => {
            const categoryInsights = insightsByCategory[category.key];
            if (!categoryInsights || categoryInsights.length === 0) return null;
            return (
              <Grid item xs={12} sm={6} md={4} key={category.key}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      mb={1.5}
                      sx={{
                        pb: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      {React.cloneElement(category.icon, {
                        color: "primary",
                        sx: { fontSize: "1.8rem" },
                      })}
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{
                          fontWeight: 600,
                          color: "primary.dark",
                        }}
                      >
                        {category.title}
                      </Typography>
                    </Stack>
                    <List dense disablePadding>
                      {categoryInsights.map((insight) => (
                        <ListItem
                          key={insight.key}
                          sx={{
                            py: insight.isDivider ? 0.75 : 0.5,
                            px: 1.5,
                            borderRadius: 1,
                            backgroundColor: insight.isDivider
                              ? "rgba(255, 235, 235, 0.9)"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: insight.isDivider
                                ? "rgba(255, 235, 235, 0.9)"
                                : "action.hover",
                            },
                            "&:not(:last-child)": {
                              borderBottom: "1px solid",
                              borderColor: "divider",
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 38,
                              mt: 0.3,
                              color: `${insight.colorProp || "action"}.main`,
                            }}
                          >
                            {React.cloneElement(
                              iconMapMUI[insight.iconName] || <InfoIcon />,
                              { fontSize: "small" }
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primaryTypographyProps={{
                              variant: "body2",
                              lineHeight: 1.2,
                              fontSize: "0.95rem",
                              color: insight.isDivider
                                ? "error.main"
                                : "text.secondary",
                              fontWeight: insight.isDivider ? 600 : 400,
                              sx: insight.isDivider
                                ? {
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }
                                : {},
                            }}
                            primary={insight.text}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
      </Grid>

      {Object.values(insightsByCategory).every((arr) => arr.length === 0) && (
        <Alert
          severity="info"
          sx={{
            mt: 2,
            borderRadius: 1,
            "& .MuiAlert-icon": {
              fontSize: "1.25rem",
            },
          }}
        >
          No specific insights could be generated based on the current screener
          data.
        </Alert>
      )}
    </Box>
  );
};

PortfolioInsights.propTypes = {
  name: PropTypes.string,
  portfolioAnalysisData: PropTypes.array,
  symbolDistributionData: PropTypes.array,
  portfolioTopGainers: PropTypes.array,
  portfolioTopLosers: PropTypes.array,
  BASE_COLORS: PropTypes.array,
  lightenColor: PropTypes.func,
  formatDollarValue: PropTypes.func,
  renderCustomizedLabelWithLines: PropTypes.func,
  sectorAllocationByMarketValue: PropTypes.array,
};

export default PortfolioInsights;
